import { createLogger } from '@owl-lib/logger';
import {
  current as sessionCurrent,
  ws as sessionWs,
} from '../../api/session/client';
import { globalBus } from '../../context/event-bus';
import {
  notificationGlobalBusHandler,
  NOTIFICATION_EMIT_EVENTS,
} from '../notifications/logic';

const logger = createLogger(__filename);

export type ConnectionState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected';

const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS = 30_000;
const HEARTBEAT_MS = 25_000;
const MAX_AUTH_CHECK_ATTEMPTS = 5;

class PlatformWebSocketManager {
  private socket: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private reconnectAttempt = 0;
  private intentionalClose = false;
  private state: ConnectionState = 'idle';
  private generation = 0;
  private onStateChange?: (state: ConnectionState) => void;

  setOnStateChange(cb: (state: ConnectionState) => void) {
    this.onStateChange = cb;
  }

  private setState(next: ConnectionState) {
    if (this.state === next) {
      return;
    }

    this.state = next;
    this.onStateChange?.(next);
  }

  start() {
    this.intentionalClose = false;
    this.reconnectAttempt = 0;

    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    this.connect();
  }

  stop() {
    this.intentionalClose = true;
    this.clearReconnectTimer();
    this.clearHeartbeatTimer();

    if (this.socket) {
      try {
        this.socket.close(1000, 'intentional_shutdown');
      } catch (err) {
        logger.warn('Error closing websocket', { err });
      }
    }

    this.socket = null;
    this.setState('idle');
    this.reconnectAttempt = 0;
  }

  getState() {
    return this.state;
  }

  private connect() {
    this.clearReconnectTimer();

    if (this.socket) {
      const staleSocket = this.socket;
      staleSocket.onopen = null;
      staleSocket.onmessage = null;
      staleSocket.onerror = null;
      staleSocket.onclose = null;
      try {
        staleSocket.close();
      } catch (err) {
        logger.warn('Error closing stale websocket', { err });
      }
    }

    this.generation += 1;
    const gen = this.generation;

    this.setState(this.reconnectAttempt === 0 ? 'connecting' : 'reconnecting');
    const socket = sessionWs();
    this.socket = socket;

    socket.onopen = () => {
      if (gen !== this.generation) {
        return;
      }
      logger.info('WebSocket connection opened');
      this.setState('connected');
      this.reconnectAttempt = 0;
      this.startHeartbeat();
    };

    socket.onmessage = (event) => {
      if (gen !== this.generation) {
        return;
      }
      try {
        const data = JSON.parse(event.data);

        notificationGlobalBusHandler(data);

        globalBus.emit(NOTIFICATION_EMIT_EVENTS.Default, data);
      } catch (err) {
        logger.error('Failed to parse websocket message', {
          err,
          raw: event.data,
        });
      }
    };

    socket.onerror = (event) => {
      logger.error('WebSocket error', { event });
    };

    socket.onclose = async (event) => {
      logger.warn('WebSocket closed', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
        intentionalClose: this.intentionalClose,
      });

      this.clearHeartbeatTimer();

      if (gen !== this.generation) {
        return;
      }

      this.socket = null;

      if (this.intentionalClose) {
        this.setState('idle');
        return;
      }

      this.setState('disconnected');

      if (!window.navigator.onLine) {
        logger.info('Browser offline; waiting for network before reconnecting');
        window.addEventListener(
          'online',
          () => {
            if (!this.intentionalClose) {
              this.scheduleReconnect();
            }
          },
          { once: true }
        );
        return;
      }

      if (this.reconnectAttempt >= MAX_AUTH_CHECK_ATTEMPTS) {
        try {
          // cheap auth sanity check
          await sessionCurrent();
        } catch (err) {
          logger.warn('Session check failed during websocket reconnect', {
            err,
          });
          this.setState('idle');
          return;
        }

        if (gen !== this.generation) {
          return;
        }
      }

      this.scheduleReconnect();
    };
  }

  private scheduleReconnect() {
    if (this.intentionalClose || this.reconnectTimer) {
      return;
    }

    const delay = this.nextDelay(this.reconnectAttempt);
    this.reconnectAttempt += 1;

    logger.info('Scheduling websocket reconnect', {
      attempt: this.reconnectAttempt,
      delayMs: delay,
    });

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private nextDelay(attempt: number) {
    const exp = Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);
    const jitter = Math.floor(Math.random() * 500);
    return exp + jitter;
  }

  private startHeartbeat() {
    this.clearHeartbeatTimer();

    this.heartbeatTimer = window.setInterval(() => {
      if (this.socket?.readyState !== WebSocket.OPEN) {
        return;
      }

      try {
        this.socket.send(JSON.stringify({ event_type: 'ping' }));
      } catch (err) {
        logger.warn('Failed to send websocket heartbeat', { err });
      }
    }, HEARTBEAT_MS);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private clearHeartbeatTimer() {
    if (this.heartbeatTimer) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
}

export const platformWebSocketManager = new PlatformWebSocketManager();
